import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Dropdown,
  Typography,
  Space,
  Alert
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  HeartOutlined,
  DollarOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  TrophyOutlined,
  SafetyOutlined,
  CreditCardOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useBiometric } from '../../contexts/BiometricContext';
import { adminAPI } from '../../utils/api';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [gracePeriod, setGracePeriod] = useState(null);
  const { user, logout } = useAuth();
  const { deviceStatus, scanStatus } = useBiometric();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch grace period status on mount and every 5 minutes
  useEffect(() => {
    const fetchGracePeriodStatus = async () => {
      try {
        if (user?.role !== 'super_admin') {
          const response = await adminAPI.getGracePeriodStatus();
          setGracePeriod(response.data.gracePeriod);
        }
      } catch (error) {
        console.error('Failed to fetch grace period status:', error);
      }
    };

    fetchGracePeriodStatus();
    const interval = setInterval(fetchGracePeriodStatus, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Base menu items for all admins
  const baseMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard">Dashboard</Link>,
    },
  ];

  // Role-based menu items
  let roleSpecificItems = [];

  if (user?.role === 'super_admin') {
    // Super Admin: Manages Wellness Centers Only
    roleSpecificItems = [
      {
        key: '/centers',
        icon: <HomeOutlined />,
        label: <Link to="/centers">Wellness Centers</Link>,
      },
      {
        key: '/subscriptions',
        icon: <CreditCardOutlined />,
        label: <Link to="/subscriptions">Subscriptions</Link>,
      },
    ];
  } else {
    // Center Admin: Manages Consumers and Operations
    roleSpecificItems = [
      {
        key: '/consumers',
        icon: <TeamOutlined />,
        label: <Link to="/consumers">Consumers</Link>,
      },
      {
        key: '/attendance',
        icon: <CalendarOutlined />,
        label: <Link to="/attendance">Attendance</Link>,
      },
      {
        key: '/biometric-attendance',
        icon: <SafetyOutlined />,
        label: <Link to="/biometric-attendance">Biometric Check-In</Link>,
      },
      {
        key: '/measurements',
        icon: <HeartOutlined />,
        label: <Link to="/measurements">Health Measurements</Link>,
      },
      {
        key: '/payments',
        icon: <DollarOutlined />,
        label: <Link to="/payments">Payments</Link>,
      },
      {
        key: '/coaches',
        icon: <TrophyOutlined />,
        label: <Link to="/coaches">Coaches</Link>,
      },
    ];
  }

  const menuItems = [...baseMenuItems, ...roleSpecificItems];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const selectedKey = location.pathname === '/' ? '/dashboard' : location.pathname;

  return (
    <Layout style={{ minHeight: '100vh', overflow: 'hidden' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: '#001529',
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className="logo">
          {collapsed ? 'WA' : 'Wellness Admin'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 80 : 200 }}>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
            position: 'sticky',
            top: 0,
            zIndex: 999,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />

          <Space align="center" size={24}>
            {/* Biometric Scanner Status - Only for Center Admins */}
            {user?.role !== 'super_admin' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 12px',
                background: deviceStatus === 'ready' ? '#f6ffed' : '#fff1f0',
                border: `1px solid ${deviceStatus === 'ready' ? '#b7eb8f' : '#ffa39e'}`,
                borderRadius: '16px',
                height: 32,
              }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: deviceStatus === 'ready' ? '#52c41a' : '#ff4d4f',
                  animation: deviceStatus === 'ready' && scanStatus === 'scanning' ? 'pulse-green 1.5s infinite' : 'none'
                }} />
                <Text style={{ fontSize: '12px', color: deviceStatus === 'ready' ? '#389e0d' : '#cf1322' }}>
                  {deviceStatus === 'ready' ? (scanStatus === 'scanning' ? 'Scanner Ready' : 'Processing...') : 'Scanner Offline'}
                </Text>
                
                <style>{`
                  @keyframes pulse-green {
                    0% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7); }
                    70% { box-shadow: 0 0 0 6px rgba(82, 196, 26, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
                  }
                `}</style>
              </div>
            )}

            <Space align="center">
            <div style={{
              textAlign: 'right',
              marginRight: 16,
              lineHeight: '1.4'
            }}>
              <Text strong style={{
                fontSize: '14px',
                color: '#262626',
                display: 'block'
              }}>
                {user?.name}
              </Text>
              <Text type="secondary" style={{
                fontSize: '12px',
                color: '#8c8c8c'
              }}>
                {user?.role === 'super_admin' ? 'Super Admin' : 'Center Admin'}
                {user?.center_name && ` • ${user.center_name}`}
              </Text>
            </div>
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <Avatar
                style={{
                  backgroundColor: '#001529',
                  cursor: 'pointer',
                }}
                icon={<UserOutlined />}
              >
                {user?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
            </Dropdown>
          </Space>
        </Space>
      </Header>

        {/* Grace Period Warning Banner */}
        {gracePeriod?.active && (
          <Alert
            message={
              <Space>
                <ClockCircleOutlined style={{ fontSize: '16px' }} />
                <span style={{ fontWeight: 600 }}>
                  Payment Pending Approval - {gracePeriod.daysRemaining} Day{gracePeriod.daysRemaining !== 1 ? 's' : ''} Remaining
                </span>
              </Space>
            }
            description={
              <div>
                Your payment is under verification by the super admin. You have temporary access until{' '}
                <strong>{new Date(gracePeriod.expiresAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</strong>.
                {gracePeriod.daysRemaining <= 1 && (
                  <div style={{ marginTop: 8 }}>
                    ⚠️ Your access will be blocked if the payment is not approved within {gracePeriod.daysRemaining} day{gracePeriod.daysRemaining !== 1 ? 's' : ''}.
                  </div>
                )}
              </div>
            }
            type="warning"
            showIcon
            closable={false}
            style={{
              margin: 0,
              borderRadius: 0,
              borderLeft: 'none',
              borderRight: 'none',
            }}
          />
        )}

        <Content
          style={{
            margin: '0',
            padding: 0,
            height: gracePeriod?.active ? 'calc(100vh - 64px - 80px)' : 'calc(100vh - 64px)',
            overflow: 'auto',
            background: '#f0f2f5',
          }}
        >
          <div style={{
            background: '#fff',
            padding: 18,
            borderRadius: 8,
            minHeight: '100%',
          }}>
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
